import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { Title } from '@seaweb/coral/components/Typography';
import { trackEnterContactForm, trackSOPTouchpoint } from 'utils/ga';
import { useViewContext } from 'providers/ViewContextProvider';
import { useLocaleContext } from 'providers/LocaleProvider';
import withIgnoreTest from './IgnoreTest';

const StyledLogo = styled.img`
  width: 152px;
  height: 50px;
  object-fit: contain;
  color: #1b92f5;
`;

const StyledTitle = styled(Title)`
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 28px;
  align-items: center;
  color: #fff;
  > * {
    margin-right: 8px;
  }
`;

const FooterBlock = styled.div`
  display: flex;
  background-color: #001536;
  position: relative;
  padding: 72px 0;
  justify-content: center;
  min-height: 288px;

  @media screen and (max-width: 600px) {
    padding: 45px 0;
  }
`;

const WebCopyright = styled.div`
  text-align: center;
  position: absolute;
  width: 100%;
  bottom: 0;
  left: 0;
  opacity: 0.5;
  color: #fff;
  padding: 31px 0;
`;

const MobileCopyright = styled.div`
  opacity: 0.5;
  color: #fff;

  @media screen and (max-width: 600px) {
    text-align: center;
  }
`;

const Column = styled.div`
  margin: 0 100px 40px 0;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 600px) {
    margin: 0 40px 40px 0;
  }
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`;

const FooterBody = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  padding: 0 32px;
  justify-content: center;
`;

const LinkSection = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
`;

const StyledLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 16px;
  font-size: 12px;
  cursor: pointer;
`;

const StyledInternalLink = styled.a`
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 16px;
  font-size: 12px;
  cursor: pointer;
`;

const getStatusLink = () => {
  if (process.env.ENV === 'live') {
    return 'https://status.seatalk.io';
  }

  if (process.env.ENV === 'liveish') {
    return 'https://status.staging.seatalk.io';
  }

  return 'https://status.test.seatalk.io';
};

function Footer() {
  const { locale: t, host } = useLocaleContext();
  const { isMobileView } = useViewContext();
  let seatalkWebHost = 'seatalkweb.com/';
  let seatalkOaHost = 'oa.seatalk.sea.com/';
  const seatalkOpenPlatformHost = 'open.seatalk.io/';
  if (
    ['development', 'test'].includes(process.env.NODE_ENV) ||
    process.env.ENV === 'test'
  ) {
    seatalkWebHost = 'seatalkweb.com/beta/';
    seatalkOaHost = 'oa.test.seatalk.sea.com/';
  }
  let seatalkOfficialWebHost;
  if (host?.includes('localhost')) {
    // local dev -> test env
    seatalkOfficialWebHost = 'test.seatalk.io';
  } else if (host?.includes('haiserve.com')) {
    // help.*?.haiserve.com -> official.*?.haiserve.com
    seatalkOfficialWebHost = host?.replace('help', 'official');
  } else {
    // help.*?.seatalk.io -> *?.seatalk.io
    seatalkOfficialWebHost = host?.replace('help.', '') || 'seatalk.io';
  }

  return (
    <FooterBlock>
      <FooterBody style={{ flexDirection: isMobileView ? 'column' : 'row' }}>
        {isMobileView && (
          <StyledLogo
            src="/images/seatalk_icon_footer.svg"
            style={{ marginBottom: '32px' }}
          />
        )}

        <LinkSection>
          {!isMobileView && (
            <Column>
              <StyledTitle>{t('hc.footer.download')}</StyledTitle>
              <Items>
                <StyledLink href="https://itunes.apple.com/us/app/seatalk/id1336516770?mt=8">
                  {t('hc.footer.download.ios')}
                </StyledLink>
                <StyledLink href="https://play.google.com/store/apps/details?id=com.seagroup.seatalk">
                  {t('hc.footer.download.android')}
                </StyledLink>
                <StyledLink href="https://portal.haiserve.com/package/beta">
                  {t('hc.footer.download.android_cn')}
                </StyledLink>
                <StyledLink href="https://static.cdn.haiserve.com/seatalk/static/client/desktop/global/darwin-x64-prod-2.2.2/SeaTalk-2.2.2.dmg">
                  {t('hc.footer.download.mac')}
                </StyledLink>
                <StyledLink href="https://static.cdn.haiserve.com/seatalk/static/client/desktop/global/win32-x64-prod-2.2.2/SeaTalk-Setup-2.2.2.exe">
                  {t('hc.footer.download.windows')}
                </StyledLink>
              </Items>
            </Column>
          )}

          <Column>
            <StyledTitle>{t('hc.footer.product')}</StyledTitle>
            <Items>
              <StyledLink
                href={`https://${seatalkOfficialWebHost}/features/communication`}
              >
                {t('hc.footer.product.features')}
              </StyledLink>
              <StyledLink href={`https://${seatalkWebHost}`}>
                {t('hc.footer.product.seatalk_web')}
              </StyledLink>
              <StyledLink href={`https://${seatalkOaHost}`}>
                {t('hc.footer.product.seatalk_admin')}
              </StyledLink>
              <StyledLink
                href={`https://${seatalkOpenPlatformHost}`}
                onClick={trackSOPTouchpoint}
              >
                {t('hc.footer.product.seatalk_open_platform')}
              </StyledLink>
            </Items>
          </Column>

          <Column>
            <StyledTitle>{t('hc.footer.support')}</StyledTitle>
            <Items>
              <Link href="/">
                <StyledInternalLink>{t('hc.help_center')}</StyledInternalLink>
              </Link>
              <Link href="/contact" onClick={trackEnterContactForm}>
                <StyledInternalLink>
                  {t('hc.contact.contact_us')}
                </StyledInternalLink>
              </Link>
              <StyledLink href={getStatusLink()} target="_blank">
                {t('hc.footer.support.service_status')}
              </StyledLink>
            </Items>
          </Column>

          <Column>
            <StyledTitle>{t('hc.footer.legal')}</StyledTitle>
            <Items>
              <StyledLink
                href={`https://${seatalkOfficialWebHost}/terms/corporate`}
              >
                {t('hc.footer.legal.corporate_terms')}
              </StyledLink>
              <StyledLink
                href={`https://${seatalkOfficialWebHost}/terms/individual`}
              >
                {t('hc.footer.legal.user_terms')}
              </StyledLink>
              <StyledLink
                href={`https://${seatalkOfficialWebHost}/terms/privacy`}
              >
                {t('hc.footer.legal.privacy_policy')}
              </StyledLink>
            </Items>
          </Column>
        </LinkSection>

        {!isMobileView && (
          <StyledLogo
            src="/images/seatalk_icon_footer.svg"
            style={{ marginBottom: '40px' }}
          />
        )}

        {isMobileView ? (
          <MobileCopyright>
            {t('hc.footer.copy_right_template', {
              current_year: new Date().getFullYear(),
            })}
          </MobileCopyright>
        ) : (
          <WebCopyright>
            {t('hc.footer.copy_right_template', {
              current_year: new Date().getFullYear(),
            })}
          </WebCopyright>
        )}
      </FooterBody>
    </FooterBlock>
  );
}

export default withIgnoreTest(Footer);
